// Paper-space (print sheets) geometry: paper sizes, sheet/viewport creation, and
// the ONE frame that places the model inside a sheet viewport (`viewportFrame`:
// model → paper and back) — the drawing, a paper dimension, a pan and a hit test
// all go through it. The model inside a viewport reuses the world→screen
// Viewport; the page itself uses a simple Y-down paper→screen mapping (PaperView).
// A rectangle's handles and its resize are one each too (`rectHandles`,
// `resizeRect`), for a viewport, a sheet box and a title-block object alike.
// DOM-free (unit-tested).

import type {
  Bounds,
  Lang,
  PaperFormat,
  Sheet,
  SheetViewport,
  Vec2,
} from '../shared/types';
import { NAME_LANGS } from '../shared/names';
import { Viewport } from './viewport';

/** Portrait paper sizes in millimetres. */
export const PAPER_SIZES: Record<Exclude<PaperFormat, 'custom'>, { w: number; h: number }> = {
  A0: { w: 841, h: 1189 },
  A1: { w: 594, h: 841 },
  A2: { w: 420, h: 594 },
  A3: { w: 297, h: 420 },
  A4: { w: 210, h: 297 },
};

/** Common architectural scales, label → paper-mm per world-mm. */
export const SHEET_SCALES: Array<[string, number]> = [
  ['1:10', 1 / 10], ['1:20', 1 / 20], ['1:25', 1 / 25], ['1:50', 1 / 50],
  ['1:100', 1 / 100], ['1:200', 1 / 200], ['1:500', 1 / 500],
];

/** Nearest scale label for a scale value (e.g. 0.02 → "1:50"), or a "1:N" string. */
export function scaleLabel(scale: number): string {
  if (scale <= 0) return '—';
  const hit = SHEET_SCALES.find(([, s]) => Math.abs(s - scale) < s * 0.001);
  return hit ? hit[0] : `1:${Math.round(1 / scale)}`;
}

/** Paper size (mm) for a format + orientation. */
export function paperSize(sheet: Pick<Sheet, 'paper' | 'orientation' | 'widthMm' | 'heightMm'>): { w: number; h: number } {
  if (sheet.paper === 'custom') return { w: sheet.widthMm, h: sheet.heightMm };
  const s = PAPER_SIZES[sheet.paper];
  return sheet.orientation === 'landscape' ? { w: s.h, h: s.w } : { w: s.w, h: s.h };
}

// Unique ids. A monotonic counter alone is not enough: it resets to 0 on every
// app launch, so after reopening a saved project (whose sheet/viewport ids are
// preserved) a freshly created sheet could regenerate an id that already exists.
// The random suffix makes collisions with loaded or concurrent ids effectively
// impossible.
let idSeq = 0;
function uid(prefix: string): string {
  idSeq = (idSeq + 1) % 1_000_000;
  return `${prefix}-${idSeq.toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

export function makeSheet(paper: PaperFormat = 'A4', orientation: 'portrait' | 'landscape' = 'landscape'): Sheet {
  const s: Sheet = { id: uid('sheet'), paper, orientation, widthMm: 0, heightMm: 0, viewports: [] };
  const sz = paperSize({ ...s });
  s.widthMm = sz.w; s.heightMm = sz.h;
  return s;
}

type SheetNaming = Pick<Sheet, 'name' | 'num' | 'lightingRoom'>;
type TrIn = (key: string, lang: Lang) => string;

/** A sheet's name in `lang` (docs/claude/names.md): the name entered for it,
 *  as typed; else its default — "Sheet <num>", or a room's lighting plan,
 *  "Lighting plan · <the room as the plan labels it>", a copy of one " (num)". */
export function sheetName(sh: SheetNaming, lang: Lang, trIn: TrIn, roomLabel: (id: string, lang: Lang) => string | undefined): string {
  if (sh.name?.trim()) return sh.name;
  if (sh.lightingRoom) return `${trIn('Lighting plan', lang)} · ${roomLabel(sh.lightingRoom, lang) ?? ''}${sh.num && sh.num > 1 ? ` (${sh.num})` : ''}`;
  return `${trIn('Sheet', lang)} ${sh.num ?? 1}`;
}

/** The number a new sheet's default takes: one past the highest its kind has
 *  (plain sheets, or one room's lighting plans). */
export function nextSheetNum(sheets: readonly SheetNaming[], lightingRoom?: string): number {
  const own = sheets.filter((s) => (s.lightingRoom ?? undefined) === lightingRoom && !s.name?.trim());
  return own.length ? Math.max(...own.map((s) => s.num ?? 1)) + 1 : 1;
}

/** The naming of a copy of `sh` among `sheets`: an entered name gets " (2)",
 *  " (3)"… the first free; a default, its kind's next number. */
export function copiedSheetName(sh: SheetNaming, sheets: readonly SheetNaming[]): SheetNaming {
  const out: SheetNaming = sh.lightingRoom ? { lightingRoom: sh.lightingRoom } : {};
  if (sh.name?.trim()) {
    const taken = new Set(sheets.map((s) => s.name));
    let k = 2;
    while (taken.has(`${sh.name} (${k})`)) k++;
    out.name = `${sh.name} (${k})`;
  } else out.num = nextSheetNum(sheets, sh.lightingRoom);
  return out;
}

/** A sheet saved when the app gave it a name as text ("Sheet 3", in any of the
 *  three languages): its default again, that number kept. Once, on load. */
export function settleSheetName(sh: Sheet, trIn: TrIn): void {
  if (sh.num != null || !sh.name) return;
  for (const l of NAME_LANGS) {
    const word = trIn('Sheet', l).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const m = new RegExp(`^${word}\\s+(\\d+)$`).exec(sh.name.trim());
    if (m) { sh.num = Number(m[1]); delete sh.name; return; }
  }
}

/** A viewport filling most of the page, showing (and fitted to) the given model
 *  bounds. Falls back to 1:50 centred on the origin when there is no content. */
export function makeViewport(sheet: Sheet, modelBounds: Bounds | null): SheetViewport {
  const m = 15; // page margin (mm)
  const vp: SheetViewport = {
    id: uid('vp'),
    x: m, y: m, w: Math.max(20, sheet.widthMm - 2 * m), h: Math.max(20, sheet.heightMm - 2 * m),
    centerWorld: { x: 0, y: 0 }, scale: 1 / 50,
    locked: false, // new viewports start unlocked; one saved without the flag reads locked
  };
  if (modelBounds) fitViewportToBounds(vp, modelBounds);
  return vp;
}

/** Set a viewport's scale + centre so the given world bounds fit inside it. */
export function fitViewportToBounds(vp: SheetViewport, bounds: Bounds, margin = 0.08): void {
  const bw = bounds.max.x - bounds.min.x, bh = bounds.max.y - bounds.min.y;
  if (bw <= 0 || bh <= 0) return;
  const sx = (vp.w * (1 - 2 * margin)) / bw, sy = (vp.h * (1 - 2 * margin)) / bh;
  const s = Math.min(sx, sy);
  if (s > 0 && isFinite(s)) vp.scale = s;
  vp.centerWorld = { x: (bounds.min.x + bounds.max.x) / 2, y: (bounds.min.y + bounds.max.y) / 2 };
}

/** Paper→screen mapping (Y-down, top-left paper origin). */
export interface PaperView { scale: number; ox: number; oy: number; }

/** Fit a page of the given mm size into the canvas (px). */
export function fitPaper(sheetW: number, sheetH: number, cw: number, ch: number, margin = 0.08): PaperView {
  const s = Math.min((cw * (1 - 2 * margin)) / sheetW, (ch * (1 - 2 * margin)) / sheetH) || 0.2;
  return { scale: s, ox: (cw - sheetW * s) / 2, oy: (ch - sheetH * s) / 2 };
}

/** The page's own zoom and pan over its fit to the window (zoom 1, pan 0 0:
 *  fitted). The view a hit test or a frame uses is worked out from it on demand
 *  (`paperViewOf`), never kept from the last frame. */
export interface PaperCamera { zoom: number; pan: Vec2 }
export const FITTED_PAPER: PaperCamera = { zoom: 1, pan: { x: 0, y: 0 } };
const PAPER_ZOOM_MIN = 0.2, PAPER_ZOOM_MAX = 40;

/** Where a page of this size lands in a canvas cw × ch (px) under the camera. */
export function paperViewOf(page: { widthMm: number; heightMm: number }, cw: number, ch: number, cam: PaperCamera): PaperView {
  const scale = fitPaper(page.widthMm, page.heightMm, cw, ch).scale * cam.zoom;
  return { scale, ox: (cw - page.widthMm * scale) / 2 + cam.pan.x, oy: (ch - page.heightMm * scale) / 2 + cam.pan.y };
}

/** The camera zoomed by `factor` about a screen pivot: the paper point under the
 *  pivot stays under it (the zoom held to 0.2 … 40). */
export function zoomPaperAbout(cam: PaperCamera, page: { widthMm: number; heightMm: number }, cw: number, ch: number, pivot: Vec2, factor: number): PaperCamera {
  const at = screenToPaper(paperViewOf(page, cw, ch, cam), pivot);
  const zoom = Math.min(PAPER_ZOOM_MAX, Math.max(PAPER_ZOOM_MIN, cam.zoom * factor));
  const scale = fitPaper(page.widthMm, page.heightMm, cw, ch).scale * zoom;
  return {
    zoom,
    pan: { x: pivot.x - at.x * scale - (cw - page.widthMm * scale) / 2, y: pivot.y - at.y * scale - (ch - page.heightMm * scale) / 2 },
  };
}

export function paperToScreen(v: PaperView, x: number, y: number): Vec2 { return { x: v.ox + x * v.scale, y: v.oy + y * v.scale }; }
export function screenToPaper(v: PaperView, s: Vec2): Vec2 { return { x: (s.x - v.ox) / v.scale, y: (s.y - v.oy) / v.scale }; }

export interface ScreenRect { x: number; y: number; w: number; h: number; }
export function vpScreenRect(v: PaperView, vp: SheetViewport): ScreenRect {
  return { x: v.ox + vp.x * v.scale, y: v.oy + vp.y * v.scale, w: vp.w * v.scale, h: vp.h * v.scale };
}

/** A viewport's frame: where a model point lands on the page through it, and
 *  back (paper mm Y down, the model Y up), and whether a paper point is in it. */
export interface ViewportFrame { toPaper(w: Vec2): Vec2; toModel(p: Vec2): Vec2; contains(p: Vec2): boolean }
export function viewportFrame(vp: SheetViewport): ViewportFrame {
  const cx = vp.x + vp.w / 2, cy = vp.y + vp.h / 2, s = vp.scale, c = vp.centerWorld;
  return {
    toPaper: (w) => ({ x: cx + (w.x - c.x) * s, y: cy - (w.y - c.y) * s }),
    toModel: (p) => ({ x: c.x + (p.x - cx) / s, y: c.y - (p.y - cy) / s }),
    contains: (p) => p.x >= vp.x && p.x <= vp.x + vp.w && p.y >= vp.y && p.y <= vp.y + vp.h,
  };
}

/** The world→screen Viewport that renders the model inside a sheet viewport: its
 *  frame, then the page's paper→screen. */
export function vpModelViewport(v: PaperView, vp: SheetViewport): Viewport {
  const o = viewportFrame(vp).toPaper({ x: 0, y: 0 }); // where the model's origin lands
  const m = new Viewport();
  m.scale = v.scale * vp.scale; // px per world-mm
  m.tx = v.ox + o.x * v.scale;
  m.ty = v.oy + o.y * v.scale;
  return m;
}

/** The topmost viewport holding a paper point (the last drawn wins), or null. */
export function viewportAtPaper(viewports: readonly SheetViewport[], p: Vec2): SheetViewport | null {
  for (let i = viewports.length - 1; i >= 0; i--) if (viewportFrame(viewports[i]).contains(p)) return viewports[i];
  return null;
}

/** The topmost viewport under a screen point, or null. */
export function viewportAt(v: PaperView, sheet: Sheet, s: Vec2): SheetViewport | null {
  return viewportAtPaper(sheet.viewports, screenToPaper(v, s));
}

/** The model centre after a pan from `centre0` by a paper move: the model moves
 *  with the pointer (paper Y down, the model Y up). */
export function panViewport(vp: SheetViewport, centre0: Vec2, d: Vec2): Vec2 {
  return { x: centre0.x - d.x / vp.scale, y: centre0.y + d.y / vp.scale };
}

export type HandleId =
  | 'e'
  | 'n'
  | 'ne'
  | 'nw'
  | 's'
  | 'se'
  | 'sw'
  | 'w';

/** A rectangle's corner and edge handles (in whatever space it is given). */
export function rectHandles(r: ScreenRect): Array<{ id: HandleId; x: number; y: number }> {
  const mx = r.x + r.w / 2, my = r.y + r.h / 2, r2 = r.x + r.w, b2 = r.y + r.h;
  return [
    { id: 'nw', x: r.x, y: r.y }, { id: 'n', x: mx, y: r.y }, { id: 'ne', x: r2, y: r.y },
    { id: 'e', x: r2, y: my }, { id: 'se', x: r2, y: b2 }, { id: 's', x: mx, y: b2 },
    { id: 'sw', x: r.x, y: b2 }, { id: 'w', x: r.x, y: my },
  ];
}

/** Resize-handle screen points of a viewport. */
export function vpHandles(v: PaperView, vp: SheetViewport): Array<{ id: HandleId; x: number; y: number }> {
  return rectHandles(vpScreenRect(v, vp));
}

/** A rectangle dragged by one of its handles to `p` (paper mm): the opposite
 *  edges stay, and it keeps at least `min` each way. */
export function resizeRect(r: ScreenRect, id: HandleId, p: Vec2, min: number): ScreenRect {
  let { x, y, w, h } = r;
  const x1 = x + w, y1 = y + h;
  if (id.includes('w')) { x = Math.min(p.x, x1 - min); w = x1 - x; }
  if (id.includes('e')) { w = Math.max(min, p.x - x); }
  if (id.includes('n')) { y = Math.min(p.y, y1 - min); h = y1 - y; }
  if (id.includes('s')) { h = Math.max(min, p.y - y); }
  return { x, y, w, h };
}

/** A viewport dragged by a handle (paper mm), at least 20 mm each way. */
export function resizeViewport(vp: SheetViewport, id: HandleId, paper: Vec2, min = 20): void {
  Object.assign(vp, resizeRect(vp, id, paper, min));
}
